import { Button, Grid, MenuItem } from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { ChangeEvent } from 'react';

import { composeFieldValidators } from '@jebel/utils';

import { FormSelect, FormTextField } from 'shared/components/ui';
import { FIELDS_CONFIG_USER } from 'shared/constants';
import { useUserByEmailLazyQuery } from 'shared/graphql';
import { UserInvitationParsed } from 'shared/hooks';
import { isValidEmail, required } from 'shared/utils/form';
import { sendToSentry } from 'shared/utils/sentry';

import { Container, HeaderText, Header } from './StyledComponents';

interface Props {
  index: number;
  name: string;
  onRemove: () => void;
}

const mapToMenuItem = (item: { label: string; value: string }) => (
  <MenuItem key={item.value} value={item.value}>
    {item.label}
  </MenuItem>
);

const ERROR_EMAIL_EXISTS_MESSAGE = 'Email address is already registered.';
/** Store a list of emails and if they can be used. */
const RESPONSE_EMAIL_CACHE: Record<string, boolean> = {};

export function InviteMemberItem(props: Props) {
  const [field] = useField<UserInvitationParsed>(`${props.name}.${props.index}`);
  const [fetchEmail] = useUserByEmailLazyQuery();

  const { setFieldValue } = useFormikContext();

  const FIELD_FIRST_NAME = `${field.name}.firstname`;
  const FIELD_LAST_NAME = `${field.name}.lastname`;
  const FIELD_EMAIL = `${field.name}.email`;
  const FIELD_AFFILIATION = `${field.name}.affiliation`;
  const FIELD_AFFILIATION_DESCRIPTION = `${field.name}.affiliationDescription`;
  const FIELD_GRADUATING_YEAR = `${field.name}.graduatingYear`;

  const onAffiliationChange = (event: ChangeEvent<{ name?: string; value: unknown }>) => {
    setFieldValue(FIELD_AFFILIATION, event.target.value);
    setFieldValue(FIELD_AFFILIATION_DESCRIPTION, '');
    setFieldValue(FIELD_GRADUATING_YEAR, '');
  };

  const canRegisterEmail = async (email: string) => {
    if (!email) {
      return;
    }

    const cache = RESPONSE_EMAIL_CACHE[email];

    if (typeof cache === 'boolean') {
      return cache;
    }

    try {
      const response = await fetchEmail({ variables: { email } });
      const canBeUsed = !response.data?.user;

      RESPONSE_EMAIL_CACHE[email] = canBeUsed;
      return canBeUsed;
    } catch (err) {
      sendToSentry(err);
    }

    return true;
  };

  const isRegistableEmail = async (email: string) => {
    const canBeUsed = await canRegisterEmail(email);

    if (canBeUsed) {
      return;
    }

    return ERROR_EMAIL_EXISTS_MESSAGE;
  };

  return (
    <Container>
      <Header>
        <HeaderText variant="subtitle2">Invite #{props.index + 1}</HeaderText>

        {props.index > 0 && (
          <Button color="secondary" onClick={props.onRemove}>
            Remove
          </Button>
        )}
      </Header>

      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          <FormTextField
            fieldProps={{ name: FIELD_FIRST_NAME, validate: required }}
            inputProps={{ label: 'First Name', variant: 'outlined', fullWidth: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormTextField
            fieldProps={{ name: FIELD_LAST_NAME, validate: required }}
            inputProps={{ label: 'Last Name', variant: 'outlined', fullWidth: true }}
          />
        </Grid>
      </Grid>

      <FormTextField
        inputProps={{ label: 'Email', variant: 'outlined' }}
        fieldProps={{
          name: FIELD_EMAIL,
          validate: composeFieldValidators(isValidEmail, isRegistableEmail),
        }}
      />

      <FormSelect
        fieldProps={{ name: FIELD_AFFILIATION, validate: required }}
        selectProps={{ label: 'Affiliation', variant: 'outlined', onChange: onAffiliationChange }}
      >
        {FIELDS_CONFIG_USER.affiliation.map(mapToMenuItem)}
      </FormSelect>

      {field.value.affiliation === 'other' && (
        <FormTextField
          fieldProps={{ name: FIELD_AFFILIATION_DESCRIPTION, validate: required }}
          inputProps={{ label: 'Affiliation Description', variant: 'outlined' }}
        />
      )}

      {field.value.affiliation === 'alumni' && (
        <FormSelect
          fieldProps={{ name: FIELD_GRADUATING_YEAR, validate: required }}
          selectProps={{ label: 'Graduating Year', variant: 'outlined' }}
        >
          {FIELDS_CONFIG_USER.graduatingYear.map(mapToMenuItem)}
        </FormSelect>
      )}
    </Container>
  );
}
